import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import NotificationBell from "../../components/NotificationBell";
import LanguageToggle from "../../components/LanguageToggle";
import Logo from "../../components/Logo";
import { useT } from "../../lib/i18n";
import {
  LayoutDashboard, FileText, Users, UserCircle, ListTodo, Megaphone,
  ShieldCheck, LogOut, Send, Menu,
} from "lucide-react";
import { toast } from "sonner";

const NAV_KEYS = [
  { to: "/admin", labelKey: "nav.dashboard", Icon: LayoutDashboard, end: true, testId: "admin-nav-dashboard" },
  { to: "/admin/applications", labelKey: "nav.applications", Icon: FileText, testId: "admin-nav-apps" },
  { to: "/admin/submit", labelKey: "nav.submit", Icon: Send, testId: "admin-nav-submit" },
  { to: "/admin/brokers", labelKey: "nav.brokers", Icon: Users, testId: "admin-nav-brokers" },
  { to: "/admin/borrowers", labelKey: "nav.borrowers", Icon: UserCircle, testId: "admin-nav-borrowers" },
  { to: "/admin/leads", labelKey: "nav.leads", Icon: Users, testId: "admin-nav-leads" },
  { to: "/admin/marketing-templates", labelKey: "nav.marketingTemplates", Icon: Megaphone, testId: "admin-nav-templates" },
  { to: "/admin/audit-log", labelKey: "nav.auditLog", Icon: ListTodo, testId: "admin-nav-audit" },
];

export default function AdminLayout() {
  const { logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const NAV = NAV_KEYS.map((n) => ({ ...n, label: t(n.labelKey) }));
  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login");
  };

  const NavLinks = ({ onClick, idSuffix = "" }) => (
    <>
      {NAV.map(({ to, label, Icon, end, testId }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={`${testId}${idSuffix}`}
          className={({ isActive }) =>
            `group mb-1 flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "border-l-2 border-blue-600 bg-blue-50 text-blue-700"
                : "border-l-2 border-transparent text-slate-600 hover:bg-blue-50 hover:text-blue-600"
            }`
          }
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
          {label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="portal-theme flex min-h-screen text-slate-900">
      {/* Desktop sidebar */}
      <aside className="hidden w-72 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-5">
          <Logo height={40} />
          <div className="ml-auto inline-flex items-center gap-1 rounded-sm border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-blue-700">
            <ShieldCheck className="h-3 w-3" /> Admin
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3"><NavLinks /></nav>
        <div className="border-t border-slate-200 p-3">
          <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-600" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4" strokeWidth={2} /> Log Out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur-sm sm:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile menu trigger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-slate-700 hover:bg-slate-100" data-testid="admin-mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 portal-theme bg-white" data-testid="admin-mobile-drawer">
                <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-5">
                  <Logo height={36} />
                  <div className="ml-auto inline-flex items-center gap-1 rounded-sm border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-blue-700">
                    <ShieldCheck className="h-3 w-3" /> Admin
                  </div>
                </div>
                <nav className="p-3"><NavLinks onClick={() => setMobileOpen(false)} idSuffix="-mobile" /></nav>
                <div className="border-t border-slate-200 p-3">
                  <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-slate-600 hover:bg-rose-50 hover:text-rose-600" data-testid="admin-logout-mobile-drawer">
                    <LogOut className="h-4 w-4" /> Log Out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <Logo height={32} className="md:hidden" />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageToggle />
            <NotificationBell role="admin" onNavigate={(target) => nav(target)} />
          </div>
        </header>
        <main className="animate-fade-in-up flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
